import * as moment from 'moment-timezone'
import {ICalendarTime} from '@eventops/athlinks-results-model'
import {
  isBoolean,
  isEmpty,
  keys,
  last,
  upperFirst,
  toUpper
} from 'lodash'
import { UnitLog } from './unitLog'

export interface CalendarTimeConversionArgs {
  dateFormat?: string,
  timeFormat?: string,
  meridiemFormat?: string,
  timeZoneFormat?: string,
  capitalize?: boolean,
  capitalizeDate?: boolean,
  capitalizeMeridiem?: boolean,
  capitalizeTimeZone?: boolean
}

export enum Temporality {
  PAST = 'past',
  PRESENT = 'present',
  FUTURE = 'future'
}

export interface ICalendarTimeData {
  date: string,
  time: string,
  meridiem: string,
  timeZone: string,
  temporality: Temporality,
  flatten(): string
}

interface CapitalizeFields {
  date?: boolean,
  meridiem?: boolean,
  timeZone?: boolean
}

type IncludeFields = CapitalizeFields & {
  time?: boolean
}

export class CalendarTimeData {
  static sortOrder = {
    ['date']: () => 0,
    ['time']: () => 1,
    ['meridiem']: () => 2,
    ['timeZone']: () => 3
  }

  constructor(
    public date: string,
    public time: string,
    public meridiem: string,
    public timeZone: string,
    public temporality: Temporality,
    public capitalizeFields: CapitalizeFields = {
      date: true,
      meridiem: true,
      timeZone: true
    }
  ) {
    if (this.capitalizeFields.date) {
      this.date = upperFirst(this.date)
    }
    if (this.capitalizeFields.meridiem) {
      this.meridiem = toUpper(this.meridiem)
    }
    if (this.capitalizeFields.timeZone) {
      this.timeZone = toUpper(this.timeZone)
    }
  }

  /**
   *
   * @param inc Fields to include, all are included by default
   */
  flatten(inc: IncludeFields = {
    date: true,
    time: true,
    meridiem: true,
    timeZone: true
  }): string {
    const sorter = CalendarTimeData.sortOrder
    const checkInc = (key: string) =>
      isBoolean(inc[key]) && inc[key]
    // capitalize by default, if it is not specified
    const checkCap = (key: string) =>
      inc[key] && this.capitalizeFields[key]
    return Object.keys(inc).filter(key => checkInc(key))
      .filter(key => sorter[key])
      .sort((a, b) => sorter[a]() > sorter[b]() ? 1 : -1 )
      .reduce((prev, curr) => {
        if (curr === 'time' && last(prev) === 'date') {
          return prev.concat([
            ',',
            curr
          ])
        } else {
          return prev.concat([curr])
        }
      }, [])
      .map(key => !this[key] ? key :
        checkCap(key) ?
        key === 'date' ?
          upperFirst(this[key]) :
          this[key].toUpperCase() :
        this[key].toLowerCase())
      .reduce((prev, curr) => {
        if (curr === ',' || isEmpty(prev)) {
          return prev.concat([curr])
        } else {
          return prev.concat([
            ' ',
            curr
          ])
        }
      }, [])
      .join('')
  }

  toString(): string {
    return this.flatten()
  }
}

export enum CommonDateFormats {
  MONTH_DAY_YEAR = 'MMMM D YYYY',
  MONTH_DAY_YEAR_SLASHES = 'M/D/YYYY',
  DAY_MONTH_YEAR = 'D MMMM YYYY',
  DAY_MONTH_YEAR_SLASHES = 'D/M/YYYY'
}

export const guessedTimeZone = moment.tz.guess()

const temporalityMapper = {
  [Temporality.PAST]:
    (testDate: moment.Moment, today: moment.Moment) =>
      testDate.isBefore(today, 'day'),
  [Temporality.PRESENT]:
    (testDate: moment.Moment, today: moment.Moment) =>
      testDate.isSame(today, 'day'),
  [Temporality.FUTURE]:
    (testDate: moment.Moment, today: moment.Moment) =>
      testDate.isAfter(today, 'day')
}

export const determineTemporality = (
  date: moment.Moment,
  timeZone: string
): Temporality => {
  const today = moment().tz(timeZone)
  return keys(temporalityMapper)
    .reduce((temporality, tName) =>
      (temporalityMapper[tName](date, today))
        ? tName : temporality
    , Temporality.PAST) as Temporality

}

export function convertCalendarTime(
  calendarTime: ICalendarTime,
  args: CalendarTimeConversionArgs = {}
): ICalendarTimeData {
  const {
    timeInMillis,
    timeZone
  } = calendarTime
  const {
    dateFormat = CommonDateFormats.MONTH_DAY_YEAR,
    timeFormat = 'h:mm',
    meridiemFormat = 'a',
    timeZoneFormat = 'z',
    capitalize = true,
    capitalizeDate = capitalize,
    capitalizeMeridiem = capitalize,
    capitalizeTimeZone = capitalize
  } = args
  let tz = timeZone
  if (isEmpty(tz) || !moment.tz.zone(tz)) {
    UnitLog.warn(`No time zone passed, ` +
      `using guessed time zone: '${guessedTimeZone}'.`)
    tz = guessedTimeZone
  }
  const dateTime = moment(timeInMillis).tz(tz)
  return new CalendarTimeData(
    dateTime.format(dateFormat),
    dateTime.format(timeFormat),
    dateTime.format(meridiemFormat),
    dateTime.format(timeZoneFormat),
    determineTemporality(dateTime, tz),
    {
      date: capitalizeDate,
      meridiem: capitalizeMeridiem,
      timeZone: capitalizeTimeZone
    }
  )
}